/**
 * URL Listener
 *
 * This is a helpful wrapper component that reacts to a change in
 * an isOpen prop to update the url accordingly. This will
 * push a fragment onto the url (i.e. #some-fragment) and then
 * listen to popstate events on the window and call the onClose method.
 * This allows components to update when users use the native back button
 * in their browser.
 *
 * @module    url-listener
 * @author    Matthew Hamlin <mhamlin@wayfair.com>
 * @copyright 2018 Wayfair LLC - All rights reserved
 */
import {Component} from 'react';
import PropTypes from 'prop-types';

// A helper method to determine if we can use pushState (IE9 doesn't support it)
const canUseNativeHistory = () =>
  window && window.history && typeof window.history.pushState === 'function';

// Generates psuedo-random numbers and then converts to a string
const getRandomString = () => Math.random().toString(36).substring(2);

export default class URLListener extends Component {
  static propTypes = {
    // The handler called when a user goes back using either the back button
    // or swipe gestures
    onClose: PropTypes.func.isRequired,
    // A boolean flag indicating if we should update the URL
    isOpen: PropTypes.bool.isRequired,
    // Any children within the URL Listener
    children: PropTypes.node.isRequired,

    // Props used for dependency injection
    // So we can test the general interaction
    // resolves to window.addEventListener
    addListener: PropTypes.func,
    // resolves to window.removeEventListener
    removeListener: PropTypes.func,
    // resolves to window.history.pushState
    updateUrl: PropTypes.func,
    // resolve to window.history.back()
    goBack: PropTypes.func,
    // A window implementation used for unit tests
    getWindow: PropTypes.func,
  };

  static defaultProps = {
    addListener(eventName, handler) {
      window.addEventListener(eventName, handler);
    },
    removeListener(eventName, handler) {
      window.removeEventListener(eventName, handler);
    },
    updateUrl(urlFragment) {
      if (canUseNativeHistory()) {
        window.history.pushState({}, document.title, urlFragment);
      }
    },
    goBack() {
      if (canUseNativeHistory()) {
        window.history.back();
      }
    },
    getWindow() {
      return window;
    },
  };

  componentDidMount() {
    // Add our popstate listener
    this.props.addListener('popstate', this.handlePopstate);
    // If we are already open, then push the fragment onto the url
    if (this.props.isOpen) {
      this.props.updateUrl(this.urlFragment);
    }
  }

  componentDidUpdate(prevProps) {
    const isOpening = this.props.isOpen && !prevProps.isOpen;
    const isClosing = !this.props.isOpen && prevProps.isOpen;
    const win = this.props.getWindow();
    const currentUrl = win.location.href;
    // If we are changing from a closed to open state, update the url
    if (isOpening) {
      this.props.updateUrl(this.urlFragment);
    }
    // if we are changing from an open to closed state,
    // and the current url includes our fragment
    // go back to the previous url
    if (
      isClosing &&
      // if we didn't update the url for this drawer then we bail on going back
      currentUrl.includes(this.urlFragment)
    ) {
      this.props.goBack(this.urlFragment);
    }
  }

  componentWillUnmount() {
    const win = this.props.getWindow();
    const currentUrl = win.location.href;
    // if we are unmounting, and we still have the fragment in the url
    // then lets clean up and go back
    if (currentUrl.includes(this.urlFragment)) {
      this.props.goBack(this.urlFragment);
    }
    this.props.removeListener('popstate', this.handlePopstate);
  }

  // A counter to ensure that the url fragments are different for each instance
  static counter = 0;

  urlFragment = `#${getRandomString()}-${URLListener.counter++}`;

  handlePopstate = () => {
    if (!this.props.isOpen) {
      // If we are not open then bail on updating the url
      return;
    }
    const win = this.props.getWindow();
    const currentUrl = win.location.href;
    // If we no longer have the current url fragment,
    // then we need to call the close handlers
    if (!currentUrl.includes(this.urlFragment)) {
      this.props.onClose(this.urlFragment);
    }
  };

  render() {
    return this.props.children;
  }
}
